import {
  ColorScheme,
  MermaidElementModel,
  type MermaidFitMode,
} from "@blocksuite/affine-model";
import {
  ActionPlacement,
  MermaidActionProvider,
  type MermaidExternalAction,
  NotificationProvider,
  ThemeProvider,
  type ToolbarContext,
  type ToolbarModuleConfig,
  ToolbarModuleExtension,
  translate,
} from "@blocksuite/affine-shared/services";
import {
  convertMermaidGraphToCanvasSnapshotInTransaction,
  parseMermaidGraphIR,
} from "@blocksuite/affine-shared/utils";
import {
  type MenuItem,
  renderMenu,
} from "@blocksuite/affine-widget-edgeless-toolbar";
import { Bound } from "@blocksuite/global/gfx";
import {
  CodeIcon,
  CopyIcon,
  EditIcon,
  ExpandFullIcon,
  ExpandIcon,
  ExpandWideIcon,
  ExportToPngIcon,
  ExportToSvgIcon,
  ResetIcon,
  SplitViewIcon,
  ViewIcon,
} from "@blocksuite/icons/lit";
import { BlockFlavourIdentifier } from "@blocksuite/std";

import {
  enterMermaidEditing,
  getMermaidViewModeSignal,
  type MermaidViewMode,
  setMermaidViewMode,
  showMermaidDiagram,
} from "../editor-state.js";
import { applyMermaidFitMode } from "../fit.js";
import {
  copyMermaidText,
  downloadBlob,
  downloadMermaidText,
  exportMermaidPng,
  getExportableMermaidSvg,
} from "../interop.js";
import { getMermaidIntrinsicSize } from "../renderer/state.js";
import { trackMermaidAction } from "../telemetry.js";

const tr = (ctx: ToolbarContext, source: string) =>
  translate(ctx.std, source) ?? source;

function getSingleMermaid(ctx: ToolbarContext) {
  const models = ctx.getSurfaceModelsByType(
    MermaidElementModel,
  ) as MermaidElementModel[];
  return models.length === 1 && ctx.getSurfaceModels().length === 1
    ? models[0]
    : null;
}

function setMode(
  ctx: ToolbarContext,
  model: MermaidElementModel,
  mode: MermaidViewMode,
) {
  if (ctx.readonly || model.isLocked()) return;

  if (mode === "diagram") {
    showMermaidDiagram(ctx.gfx, model);
    return;
  }

  setMermaidViewMode(model, mode);
  ctx.gfx.selection.set({
    elements: [model.id],
    editing: true,
  });
}

function createModeMenu(ctx: ToolbarContext, model: MermaidElementModel) {
  const current = getMermaidViewModeSignal(model).value;
  const items = [
    {
      key: tr(ctx, "Diagram"),
      value: "diagram",
      icon: ViewIcon(),
    },
    {
      key: tr(ctx, "Code"),
      value: "code",
      icon: CodeIcon(),
    },
    {
      key: tr(ctx, "Split"),
      value: "split",
      icon: SplitViewIcon(),
    },
  ] as const satisfies MenuItem<MermaidViewMode>[];

  return renderMenu({
    label: tr(ctx, "View mode"),
    items,
    currentValue: current,
    onPick: (mode) => setMode(ctx, model, mode),
  });
}

function createFitMenu(ctx: ToolbarContext, models: MermaidElementModel[]) {
  const current =
    models.every((model) => model.fitMode === models[0]?.fitMode) &&
    models[0]?.fitMode
      ? models[0].fitMode
      : "free";
  const intrinsic = models.map(getMermaidIntrinsicSize);
  const missingIntrinsic = intrinsic.some((size) => !size);

  const items = [
    {
      key: tr(ctx, "Fit content"),
      value: "content",
      icon: ExpandIcon(),
      disabled: missingIntrinsic,
    },
    {
      key: tr(ctx, "Fit width"),
      value: "width",
      icon: ExpandWideIcon(),
      disabled: missingIntrinsic,
    },
    {
      key: tr(ctx, "Free size"),
      value: "free",
      icon: ExpandFullIcon(),
    },
  ] as const satisfies MenuItem<MermaidFitMode>[];

  const onPick = (mode: MermaidFitMode) => {
    if (ctx.readonly || models.some((model) => model.isLocked())) return;
    ctx.store.captureSync();
    models.forEach((model, index) => {
      applyMermaidFitMode(model, mode, intrinsic[index]);
    });
  };

  return renderMenu({
    label: tr(ctx, "Fit"),
    items,
    currentValue: current,
    onPick,
  });
}

function notifyInteropError(ctx: ToolbarContext, error: unknown) {
  const message = error instanceof Error ? error.message : String(error);
  ctx.std.getOptional(NotificationProvider)?.notify({
    title: tr(ctx, "Mermaid export failed"),
    message,
    accent: "error",
  });
}

function notifyInteropSuccess(ctx: ToolbarContext, message: string) {
  ctx.std.getOptional(NotificationProvider)?.toast(tr(ctx, message));
}

function safeFileStem(model: MermaidElementModel) {
  return "mermaid-" + model.id.replace(/[^a-zA-Z0-9_-]/g, "-");
}

function actionInput(model: MermaidElementModel) {
  return {
    elementId: model.id,
    source: model.source.toString(),
  };
}

function externalAction(
  id: string,
  intent: MermaidExternalAction,
  labelKey: string,
  icon: ReturnType<typeof EditIcon>,
  mutates = false,
) {
  return {
    id,
    when(ctx: ToolbarContext) {
      const model = getSingleMermaid(ctx);
      if (!model) return false;
      if (mutates && (ctx.readonly || model.isLocked())) return false;
      const provider = ctx.std.getOptional(MermaidActionProvider);
      return provider?.can(intent, actionInput(model)) ?? false;
    },
    generate(ctx: ToolbarContext) {
      const model = getSingleMermaid(ctx);
      const provider = ctx.std.getOptional(MermaidActionProvider);
      if (!model || !provider) return null;
      if (mutates && (ctx.readonly || model.isLocked())) return null;

      const label = tr(ctx, labelKey);
      return {
        label,
        tooltip: label,
        icon,
        run() {
          if (mutates && (ctx.readonly || model.isLocked())) return;
          Promise.resolve(provider.run(intent, actionInput(model))).catch(
            console.error,
          );
        },
      };
    },
  };
}


function canConvertMermaidToEditable(model: MermaidElementModel) {
  try {
    const ir = parseMermaidGraphIR(model.source.toString());
    return ir.unsupported.length === 0 && ir.nodes.length > 0;
  } catch {
    return false;
  }
}

function convertMermaidToEditable(
  ctx: ToolbarContext,
  model: MermaidElementModel,
) {
  const surface = ctx.gfx.surface;
  if (!surface) {
    throw new Error("Canvas surface is unavailable.");
  }

  const source = model.source.toString();
  const ir = parseMermaidGraphIR(source);
  const bound = Bound.deserialize(model.xywh);

  ctx.store.captureSync();
  const snapshot = convertMermaidGraphToCanvasSnapshotInTransaction(
    {
      source,
      ir,
      sourceElementId: model.id,
      origin: {
        x: bound.x + bound.w + 80,
        y: bound.y,
      },
    },
    {
      addElement(type, props) {
        return surface.addElement({ type, ...props });
      },
    },
    {
      transact(fn) {
        return ctx.store.transact(fn);
      },
    },
  );
  ctx.store.captureSync();

  const createdIds = [
    ...Object.values(snapshot.result.shapeIds),
    ...Object.values(snapshot.result.connectorIds),
    ...Object.values(snapshot.result.groupIds),
  ];

  if (createdIds.length) {
    ctx.gfx.selection.set({
      elements: createdIds,
      editing: false,
    });
  }

  return snapshot;
}

export const mermaidToolbarConfig = {
  actions: [
    {
      id: "a.edit",
      when: (ctx) => {
        const model = getSingleMermaid(ctx);
        return Boolean(model && !ctx.readonly && !model.isLocked());
      },
      generate(ctx) {
        const model = getSingleMermaid(ctx);
        if (!model) return null;
        const label = tr(ctx, "Edit source");

        return {
          label,
          tooltip: label,
          icon: EditIcon(),
          run() {
            if (ctx.readonly || model.isLocked()) return;
            enterMermaidEditing(ctx.gfx, model, "code");
          },
        };
      },
    },
    {
      id: "b.mode",
      when: (ctx) => {
        const model = getSingleMermaid(ctx);
        return Boolean(model && !ctx.readonly && !model.isLocked());
      },
      content(ctx) {
        const model = getSingleMermaid(ctx);
        return model ? createModeMenu(ctx, model) : null;
      },
    },
    {
      id: "c.fit",
      when: (ctx) => {
        const models = ctx.getSurfaceModels();
        const mermaids = ctx.getSurfaceModelsByType(MermaidElementModel);
        return (
          models.length > 0 &&
          mermaids.length === models.length &&
          !ctx.readonly &&
          !mermaids.some((model) => model.isLocked())
        );
      },
      content(ctx) {
        const models = ctx.getSurfaceModelsByType(
          MermaidElementModel,
        ) as MermaidElementModel[];
        return models.length ? createFitMenu(ctx, models) : null;
      },
    },
    {
      id: "A.mermaid",
      placement: ActionPlacement.More,
      actions: [
        {
          id: "a.edit-source",
          when: (ctx) => {
            const model = getSingleMermaid(ctx);
            return Boolean(model && !ctx.readonly && !model.isLocked());
          },
          generate(ctx) {
            const model = getSingleMermaid(ctx);
            if (!model) return null;
            const label = tr(ctx, "Edit source");
            return {
              label,
              icon: EditIcon(),
              run() {
                if (ctx.readonly || model.isLocked()) return;
                enterMermaidEditing(ctx.gfx, model, "code");
              },
            };
          },
        },
        externalAction(
          "b.modify-ai",
          "modify-ai",
          "Modify with AI",
          EditIcon(),
          true,
        ),
        externalAction(
          "c.repair-ai",
          "repair-ai",
          "Repair with AI",
          ResetIcon(),
          true,
        ),
        {
          id: "d.convert-editable",
          when: (ctx) => {
            const model = getSingleMermaid(ctx);
            return Boolean(
              model &&
                !ctx.readonly &&
                !model.isLocked() &&
                canConvertMermaidToEditable(model),
            );
          },
          generate(ctx) {
            const model = getSingleMermaid(ctx);
            if (!model || !canConvertMermaidToEditable(model)) return null;
            const label = tr(ctx, "Convert to editable objects");
            return {
              label,
              icon: SplitViewIcon(),
              run() {
                if (ctx.readonly || model.isLocked()) return;
                try {
                  convertMermaidToEditable(ctx, model);
                  notifyInteropSuccess(ctx, "Converted to editable objects");
                } catch (error) {
                  notifyInteropError(ctx, error);
                }
              },
            };
          },
        },
        {
          id: "d.copy-source",
          when: (ctx) => Boolean(getSingleMermaid(ctx)),
          generate(ctx) {
            const model = getSingleMermaid(ctx);
            if (!model) return null;
            const label = tr(ctx, "Copy Mermaid source");
            return {
              label,
              icon: CopyIcon(),
              run() {
                void copyMermaidText(model.source.toString())
                  .then(() =>
                    notifyInteropSuccess(ctx, "Mermaid source copied"),
                  )
                  .catch((error) => notifyInteropError(ctx, error));
              },
            };
          },
        },
        {
          id: "e.copy-svg",
          when: (ctx) => Boolean(getSingleMermaid(ctx)),
          generate(ctx) {
            const model = getSingleMermaid(ctx);
            if (!model) return null;
            const label = tr(ctx, "Copy SVG");
            return {
              label,
              icon: CopyIcon(),
              run() {
                try {
                  const svg = getExportableMermaidSvg(model);
                  trackMermaidAction(ctx.std, "export", "copy-svg");
                  void copyMermaidText(svg)
                    .then(() => notifyInteropSuccess(ctx, "SVG copied"))
                    .catch((error) => notifyInteropError(ctx, error));
                } catch (error) {
                  notifyInteropError(ctx, error);
                }
              },
            };
          },
        },
        {
          id: "f.export-svg",
          when: (ctx) => Boolean(getSingleMermaid(ctx)),
          generate(ctx) {
            const model = getSingleMermaid(ctx);
            if (!model) return null;
            const label = tr(ctx, "Export SVG");
            return {
              label,
              icon: ExportToSvgIcon(),
              run() {
                try {
                  const svg = getExportableMermaidSvg(model);
                  trackMermaidAction(ctx.std, "export", "svg");
                  downloadMermaidText(
                    safeFileStem(model) + ".svg",
                    svg,
                    "image/svg+xml;charset=utf-8",
                  );
                  notifyInteropSuccess(ctx, "SVG exported");
                } catch (error) {
                  notifyInteropError(ctx, error);
                }
              },
            };
          },
        },
        {
          id: "g.export-png",
          when: (ctx) => Boolean(getSingleMermaid(ctx)),
          generate(ctx) {
            const model = getSingleMermaid(ctx);
            if (!model) return null;
            const label = tr(ctx, "Export PNG (2x)");
            return {
              label,
              icon: ExportToPngIcon(),
              run() {
                trackMermaidAction(ctx.std, "export", "png-2x");
                void exportMermaidPng(
                  model,
                  2,
                  ctx.std.get(ThemeProvider).edgelessTheme === ColorScheme.Dark,
                )
                  .then((blob) => {
                    downloadBlob(safeFileStem(model) + "@2x.png", blob);
                    notifyInteropSuccess(ctx, "PNG exported");
                  })
                  .catch((error) => notifyInteropError(ctx, error));
              },
            };
          },
        },
        {
          id: "h.export-png-1x",
          when: (ctx) => Boolean(getSingleMermaid(ctx)),
          generate(ctx) {
            const model = getSingleMermaid(ctx);
            if (!model) return null;
            const label = tr(ctx, "Export PNG (1x)");
            return {
              label,
              icon: ExportToPngIcon(),
              run() {
                trackMermaidAction(ctx.std, "export", "png-1x");
                void exportMermaidPng(
                  model,
                  1,
                  ctx.std.get(ThemeProvider).edgelessTheme === ColorScheme.Dark,
                )
                  .then((blob) => {
                    downloadBlob(safeFileStem(model) + ".png", blob);
                    notifyInteropSuccess(ctx, "PNG exported");
                  })
                  .catch((error) => notifyInteropError(ctx, error));
              },
            };
          },
        },
      ],
    },
  ],
  when(ctx) {
    const models = ctx.getSurfaceModels();
    const mermaids = ctx.getSurfaceModelsByType(MermaidElementModel);
    return models.length > 0 && mermaids.length === models.length;
  },
} as const satisfies ToolbarModuleConfig;

export const mermaidToolbarExtension = ToolbarModuleExtension({
  id: BlockFlavourIdentifier("affine:surface:mermaid"),
  config: mermaidToolbarConfig,
});
